/** Contextual help EN — BT-028 (global shell). */

/** @type {Record<string, object>} */
export const helpTopicsEn = {
  accueil: {
    key: "accueil",
    screenLabel: "Public home",
    purpose: "Discover Phase 1 Shotokan AAMC Black Dragon portal and demo scope.",
    actions: [
      "Browse dojos, instructors and public documents.",
      "Spot [DEMO] banner and slots awaiting client file.",
      "Request an account or log in with demo accounts.",
    ],
    warning: "[DEMO] data — 10 official dojos awaiting client file.",
    nextStep: "Explore dojos or log in as Hanshi for admin.",
  },
  "connected-home": {
    key: "connected-home",
    screenLabel: "Home",
    purpose: "Starting point after sign-in — understand Shotokan AAMC and start or resume Training.",
    actions: [
      "Read the welcome message adapted to your role.",
      "Start or continue guided Training.",
      "Explore the application via the dashboard if you prefer.",
    ],
    warning: "Training is never mandatory to use the software.",
    nextStep: "Open Training or the Dashboard.",
  },
  "a-propos": {
    key: "a-propos",
    screenLabel: "About",
    purpose: "Understand portal mission and Phase 1 limits.",
    actions: ["Read Hanshi / Sensei / dojo structure.", "Note no operational attendance or payments."],
    warning: "Phase 1 — not a full class or billing system.",
    nextStep: "Back to home or log in.",
  },
  connexion: {
    key: "connexion",
    screenLabel: "Log in",
    purpose: "Access a space by demo role (admin, sensei, sempai, parent, student).",
    actions: [
      "Use demo username/password (listed on screen).",
      "Example: admin/demo, sensei/demo, etudiant/demo.",
      "Switch FR/EN before login if needed.",
    ],
    warning: "Local Phase 1 accounts only — not production.",
    nextStep: "After login, use your role menu or tabs.",
  },
  "change-password": {
    key: "change-password",
    screenLabel: "Staging password change",
    purpose: "Replace the unique temporary password before accessing other staging backend areas.",
    actions: [
      "Enter the temporary password received out-of-band (never shown publicly).",
      "Choose a strong password (10+ chars, letter, digit, symbol).",
      "Confirm the new password matches.",
    ],
    warning: "Temporary staging accounts — to be replaced with official email addresses.",
    nextStep: "After success, go to your role space.",
  },
  "demande-compte": {
    key: "demande-compte",
    screenLabel: "Account request",
    purpose: "Request form — local demo storage, no automatic approval.",
    actions: ["Fill identity and desired role.", "Submit — local Phase 1 storage."],
    warning: "[DEMO] — no real automatic account creation.",
    nextStep: "Use a demo account to test immediately.",
  },
  confidentialite: {
    key: "confidentialite",
    screenLabel: "Privacy Policy",
    purpose: "Understand minimal Phase 1 scope: demo, no real data, no secure backend.",
    actions: [
      "Read demo / limits / future use sections.",
      "Note official contact coordinates are to be confirmed.",
    ],
    warning: "Demonstration platform — do not present as production with real data.",
    nextStep: "Contact page for information in simulation mode.",
  },
  contact: {
    key: "contact",
    screenLabel: "Contact",
    purpose: "Understand how to reach the organization — mock form with no real send.",
    actions: [
      "Check indicative [DEMO] email.",
      "Use simulation form (no storage).",
      "Link to account request if needed.",
    ],
    warning: "[DEMO] — no message sent to a server.",
    nextStep: "Account request or demo login.",
  },
  dojos: {
    key: "dojos",
    screenLabel: "Dojos",
    purpose: "Find and compare dojos (goal: 10 official dojos).",
    actions: ["Switch Cards / Table.", "Search, sort, filter.", "Open a dojo detail page."],
    warning: "[DEMO] slots — client file pending.",
    nextStep: "Verify responsible Sensei after client import.",
  },
  instructeurs: {
    key: "instructeurs",
    screenLabel: "Instructors",
    purpose: "See Hanshi, received leadership and Sensei — dojo links to confirm.",
    actions: ["Browse martial titles (careful overview).", "Filter by title or status."],
    warning: "Leadership received, to confirm — no external recognition shown as official.",
    nextStep: "Link Sensei ↔ dojo when client file returns.",
  },
  etudiants: {
    key: "etudiants",
    screenLabel: "Students",
    purpose: "Read-only directory — minimal initial dataset (Marcel Charette, Natalie Lavigne); [DEMO] child for Parent.",
    actions: ["Search a name.", "Filter by dojo."],
    warning: "Belts and groups marked to confirm — no complete official list yet.",
    nextStep: "Bulk import after client file received.",
  },
  documents: {
    key: "documents",
    screenLabel: "Documents",
    purpose: "Browse documents by visibility (public, members, admin).",
    actions: ["Check visibility badges.", "Black Dragon protocols need Hanshi validation."],
    warning: "Do not publish sensitive protocols without Hanshi validation.",
    nextStep: "Admin → Shotokan → Documents & Evidence for proofs.",
  },
  admin: {
    key: "admin",
    screenLabel: "Hanshi administration",
    purpose: "Steer Phase 1: organisation, activities, progression, Shotokan, administration.",
    actions: [
      "Use 6 collapsible sidebar groups.",
      "Shotokan Black Dragon for titles and Documents & Evidence.",
      "CSV import or controlled document import as needed.",
    ],
    checklist: ["Documents & Evidence is not a main link — only inside Shotokan."],
    warning: "Phase 1 — no payments or operational attendance. [DEMO] data.",
    nextStep: "Run client demo.",
  },
  import: {
    key: "import",
    screenLabel: "CSV import",
    purpose: "Load dojos, Sensei and students from CSV.",
    actions: ["Paste compliant CSV.", "Preview then confirm.", "Recommended: official + keep [DEMO]."],
    warning: "Direct Excel import not built — convert from client file.",
    nextStep: "Check Hanshi dashboard.",
  },
  shotokan: {
    key: "shotokan",
    screenLabel: "Shotokan Black Dragon",
    purpose: "Titles, recognitions, authority and integrated Documents & Evidence.",
    actions: [
      "Browse overview and sub-menus.",
      "Titles and requests — internal referential.",
      "Documents & Evidence via sub-menu (admin).",
    ],
    warning: "Internal referential — validate with authority documents. [DEMO].",
    nextStep: "Check required documents before any decision.",
  },
  "documents-preuves": {
    key: "documents-preuves",
    screenLabel: "Documents & Evidence",
    purpose: "Manage proofs, dossiers and journal — integrated in Shotokan (admin).",
    actions: ["Browse documents, dossiers, journal.", "Controlled import if needed."],
    warning: "No automatic official validation — initial status received — validation required.",
    nextStep: "Controlled import for client file metadata.",
  },
  "documents-preuves-import": {
    key: "documents-preuves-import",
    screenLabel: "Controlled import",
    purpose: "Import client document metadata without Phase 1 server storage.",
    actions: ["Enter file name.", "Preview before import.", "Confirm — received — validation required enforced."],
    warning: "File not officially stored — secure backend Phase 2+.",
    nextStep: "Check journal after import.",
  },
  sensei: {
    key: "sensei",
    screenLabel: "Sensei space",
    purpose: "Manage dojo, classes, students, progress and Shotokan (limited rights).",
    actions: [
      "Grouped nav: My dojo, My classes, My students, Progress.",
      "Shotokan Black Dragon for titles and required documents.",
      "Useful documents for dojo sharing.",
    ],
    warning: "[DEMO] — no official title grant without authority. No full admin.",
    nextStep: "Open My classes or My students.",
  },
  "sensei-shotokan": {
    key: "sensei-shotokan",
    screenLabel: "Shotokan Black Dragon (Sensei)",
    purpose: "Browse titles, submit requests, see required documents for your dojo.",
    actions: ["Overview.", "Titles and My requests.", "Required documents."],
    warning: "Internal referential — no external validation shown as official.",
    nextStep: "Complete required documents before requesting.",
  },
  sempai: {
    key: "sempai",
    screenLabel: "Sempai space",
    purpose: "Assist classes and view students — consultation only.",
    actions: [
      "Assisted classes, light attendance, events.",
      "Progression review — observe, do not validate.",
      "Shotokan read-only — required documents.",
    ],
    warning: "[DEMO] — Sempai cannot validate a grade or grant a title.",
    nextStep: "Review assisted classes.",
  },
  "sempai-shotokan": {
    key: "sempai-shotokan",
    screenLabel: "Shotokan Black Dragon (Sempai)",
    purpose: "Title path and required documents — read-only.",
    actions: ["Overview.", "Title path.", "Required documents read-only."],
    warning: "Consultation only — no approval or final validation.",
    nextStep: "Escalate to Sensei for decisions.",
  },
  "student-space": {
    key: "student-space",
    screenLabel: "My student space",
    purpose: "Overview: dojo, Sensei, quick access to tabs.",
    actions: ["My classes, events, progress, titles, documents.", "7 simple tabs at top."],
    warning: "[DEMO] — no confidential documents or admin.",
    nextStep: "Open My classes or My progress.",
  },
  "student-classes": {
    key: "student-classes",
    screenLabel: "My classes",
    purpose: "Classes you are enrolled in — demo schedules.",
    actions: ["Browse the list.", "Note [DEMO] schedules."],
    warning: "Official schedules pending — demo data.",
    nextStep: "My events for seminars.",
  },
  "student-events": {
    key: "student-events",
    screenLabel: "My events",
    purpose: "Upcoming events for your dojo.",
    actions: ["Browse the list.", "Filter if available."],
    warning: "[DEMO] calendar.",
    nextStep: "My progress for belt tracking.",
  },
  "student-progress": {
    key: "student-progress",
    screenLabel: "My progress",
    purpose: "Track belt and progress — no official certification Phase 1.",
    actions: ["See current belt and next step.", "Check light exams if shown."],
    warning: "[DEMO] progress — no official certification.",
    nextStep: "My titles for simplified Shotokan path.",
  },
  "student-titles": {
    key: "student-titles",
    screenLabel: "My titles",
    purpose: "Understand title path — no aggressive high-title requests.",
    actions: ["Read visible titles.", "Talk to my Sensei for questions."],
    warning: "Martial titles ≠ app role. [DEMO].",
    nextStep: "Back to My space.",
  },
  "student-documents": {
    key: "student-documents",
    screenLabel: "My documents",
    purpose: "Documents allowed for your profile — no authority or full proof dossiers.",
    actions: ["Open filtered Documents section.", "View only what is visible to you."],
    warning: "No confidential authority documents here. [DEMO].",
    nextStep: "Help or My space.",
  },
  parent: {
    key: "parent",
    screenLabel: "Parent space",
    purpose: "Follow your children: schedules, events, progress, allowed documents.",
    actions: ["7 tabs: My children, Schedule, Events, etc.", "View child profiles."],
    warning: "Payments not managed in Phase 1. [DEMO]. No full Shotokan access.",
    nextStep: "Schedule or Progress as needed.",
  },
  "parent-children": {
    key: "parent-children",
    screenLabel: "My children",
    purpose: "Linked child profiles — group, belt, dojo.",
    actions: ["Read summary per child.", "Note associated dojo."],
    warning: "Summary only — payments not managed. [DEMO].",
    nextStep: "Schedule for planning.",
  },
  "parent-schedule": {
    key: "parent-schedule",
    screenLabel: "Schedule",
    purpose: "Your children's class schedules.",
    actions: ["View classes and slots.", "Compare between children if several."],
    warning: "Payments not managed. [DEMO] schedules.",
    nextStep: "Upcoming events.",
  },
  "parent-events": {
    key: "parent-events",
    screenLabel: "Events",
    purpose: "Dojo events concerning your children.",
    actions: ["Browse the list.", "Note important dates."],
    warning: "Payments not managed. [DEMO].",
    nextStep: "Child progress.",
  },
  "parent-progress": {
    key: "parent-progress",
    screenLabel: "Progress",
    purpose: "Summarized child progress — belt and light exams.",
    actions: ["Read summary per child.", "Parents cannot validate."],
    warning: "[DEMO] summary — exam payments not managed.",
    nextStep: "Allowed documents.",
  },
  "parent-documents": {
    key: "parent-documents",
    screenLabel: "Documents",
    purpose: "Documents allowed for parent — no proof dossiers.",
    actions: ["View visible documents.", "Contact dojo for more."],
    warning: "No authority documents. [DEMO].",
    nextStep: "Communications.",
  },
  "parent-comms": {
    key: "parent-comms",
    screenLabel: "Communications",
    purpose: "Simple dojo ↔ parent messages — Phase 1 demo consultation, real sending in Phase 2 if required.",
    actions: ["Read demo communications.", "Phase 2+ for real sending."],
    warning: "[DEMO] — communications not operational.",
    nextStep: "Back to My children.",
  },
  formation: {
    key: "formation",
    screenLabel: "Training",
    purpose: "Hanshi and Sensei guided paths with persistent progress — plus static previews for other roles.",
    actions: [
      "Continue my training or open a step.",
      "Review the Shotokan AAMC discovery tour (7 screens).",
      "Use See → Understand → Do in each step.",
      "Leave training anytime — progress is saved.",
    ],
    warning: "Training mode — sensitive actions are read-only or demo.",
    nextStep: "Continue your role path.",
  },
  aide: {
    key: "aide",
    screenLabel: "Help center",
    purpose: "Help home page — the header Help button remains the main flow for the current screen.",
    actions: [
      "Open Training for a role-based guided path.",
      "Use demo accounts (admin/demo, sensei/demo…) to explore spaces.",
      "Browse Dojos, Documents and Protocols for the public scope.",
      "Use the header Help button for contextual help on the active screen.",
    ],
    warning: "[DEMO] — Phase 1 educational content, no additional business module.",
    nextStep: "Return to your working screen or open Training.",
  },
  "mes-informations": {
    key: "mes-informations",
    screenLabel: "My profile",
    purpose: "Signed-in account summary — display name, profile photo and app role.",
    actions: [
      "Check the displayed name and role.",
      "Change your photo from the header avatar (upload or camera when available).",
      "Understand that demo accounts do not change official data.",
    ],
    warning: "[DEMO] — no password change or official profile update in Phase 1.",
    nextStep: "Return to My space or use the Mode switcher if available.",
  },
};

export const helpFallbackEn = helpTopicsEn.accueil;
